import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, Check, X } from 'lucide-react';

const DynamicFieldsStep = ({ formData, updateFormData }) => {
    const [isAddingField, setIsAddingField] = useState(false);
    const [newField, setNewField] = useState({
        fieldName: '',
        fieldType: 'text',
        validation: 'required',
        placeholder: '',
        helpText: '',
        options: '',
    });

    const fieldTypeOptions = [
        { value: 'text', label: 'Text', icon: '📝' },
        { value: 'email', label: 'Email', icon: '📧' },
        { value: 'phone', label: 'Phone Number', icon: '📱' },
        { value: 'number', label: 'Number', icon: '🔢' },
        { value: 'date', label: 'Date', icon: '📅' },
        { value: 'textarea', label: 'Text Area', icon: '📄' },
        { value: 'select', label: 'Dropdown', icon: '📋' },
        { value: 'checkbox', label: 'Checkbox', icon: '☑️' },
        { value: 'radio', label: 'Radio Button', icon: '🔘' },
        { value: 'file', label: 'File Upload', icon: '📎' },
    ];

    const handleAddField = () => {
        if (!newField.fieldName || !newField.fieldType) return;

        const field = {
            id: `field-${Date.now()}`,
            fieldName: newField.fieldName,
            fieldType: newField.fieldType,
            validation: newField.validation || 'required',
            placeholder: newField.placeholder,
            helpText: newField.helpText,
            options: typeof newField.options === 'string'
                ? newField.options.split(',').map(s => s.trim()).filter(Boolean)
                : newField.options,
            order: formData.participantFields.length,
            isDefault: false,
        };

        updateFormData({
            participantFields: [...formData.participantFields, field],
        });

        setNewField({
            fieldName: '',
            fieldType: 'text',
            validation: 'required',
            placeholder: '',
            helpText: '',
            options: '',
        });
        setIsAddingField(false);
    };

    const handleRemoveField = (fieldId) => {
        updateFormData({
            participantFields: formData.participantFields.filter((f) => f.id !== fieldId),
        });
    };

    const handleUpdateField = (fieldId, updates) => {
        updateFormData({
            participantFields: formData.participantFields.map((f) =>
                f.id === fieldId ? { ...f, ...updates } : f
            ),
        });
    };

    const moveField = (index, direction) => {
        const newFields = [...formData.participantFields];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newFields.length) return;

        [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];

        newFields.forEach((field, idx) => {
            field.order = idx;
        });

        updateFormData({ participantFields: newFields });
    };

    return (
        <div className="space-y-8">
            {/* Page Header */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Participant Registration Fields</h2>
                <p className="text-gray-600">
                    Customize the information you want to collect from participants
                </p>
            </div>

            {/* Existing Fields */}
            <div className="space-y-4">
                {formData.participantFields.map((field, index) => (
                    <div
                        key={field.id}
                        className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-gray-300 transition-all"
                    >
                        <div className="flex items-start space-x-4">
                            {/* Drag Handle */}
                            <div className="flex flex-col space-y-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => moveField(index, 'up')}
                                    disabled={index === 0}
                                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                                <GripVertical className="w-5 h-5 text-gray-400" />
                                <button
                                    type="button"
                                    onClick={() => moveField(index, 'down')}
                                    disabled={index === formData.participantFields.length - 1}
                                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>

                            {/* Field Info */}
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-3">
                                        <h3 className="font-semibold text-gray-900 capitalize">
                                            {field.fieldName}
                                        </h3>
                                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                            {field.fieldType}
                                        </span>
                                        <span className={`px-2 py-1 text-xs font-medium rounded ${field.validation === 'required'
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-gray-100 text-gray-700'
                                            }`}>
                                            {field.validation}
                                        </span>
                                        {field.isDefault && (
                                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                                                Default
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => handleRemoveField(field.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Remove field"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </div>

                                {field.placeholder && (
                                    <p className="text-sm text-gray-600 mb-1">
                                        Placeholder: <span className="italic">{field.placeholder}</span>
                                    </p>
                                )}

                                {field.helpText && (
                                    <p className="text-sm text-gray-500">
                                        Help: {field.helpText}
                                    </p>
                                )}

                                {field.options && field.options.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {field.options.map((opt, idx) => (
                                            <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                                {opt}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Validation Toggle */}
                            <div className="flex items-center space-x-2">
                                <button
                                    type="button"
                                    onClick={() => handleUpdateField(field.id, {
                                        validation: field.validation === 'required' ? 'optional' : 'required'
                                    })}
                                    className={`
                  relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                  ${field.validation === 'required' ? 'bg-red-500' : 'bg-gray-300'}
                `}
                                    title="Toggle Required"
                                >
                                    <span
                                        className={`
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                    ${field.validation === 'required' ? 'translate-x-6' : 'translate-x-1'}
                  `}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Add New Field */}
            {!isAddingField ? (
                <button
                    type="button"
                    onClick={() => setIsAddingField(true)}
                    className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all group"
                >
                    <div className="flex items-center justify-center space-x-2 text-gray-600 group-hover:text-blue-600">
                        <Plus className="w-5 h-5" />
                        <span className="font-medium">Add New Field</span>
                    </div>
                </button>
            ) : (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 space-y-4">
                    <h3 className="font-semibold text-gray-900 mb-4">Add New Field</h3>

                    {/* Field Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Field Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={newField.fieldName || ''}
                            onChange={(e) => setNewField({ ...newField, fieldName: e.target.value })}
                            placeholder="e.g., Phone Number, Company Name"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Field Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Field Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={newField.fieldType || 'text'}
                            onChange={(e) => setNewField({ ...newField, fieldType: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            {fieldTypeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.icon} {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Validation */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Validation
                        </label>
                        <div className="flex space-x-4">
                            <button
                                type="button"
                                onClick={() => setNewField({ ...newField, validation: 'required' })}
                                className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${newField.validation === 'required'
                                    ? 'border-red-500 bg-red-50 text-red-700'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                            >
                                Required
                            </button>
                            <button
                                type="button"
                                onClick={() => setNewField({ ...newField, validation: 'optional' })}
                                className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${newField.validation === 'optional'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                            >
                                Optional
                            </button>
                        </div>
                    </div>

                    {/* Placeholder */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Placeholder Text
                        </label>
                        <input
                            type="text"
                            value={newField.placeholder || ''}
                            onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                            placeholder="e.g., Enter your phone number"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Help Text */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Help Text
                        </label>
                        <input
                            type="text"
                            value={newField.helpText || ''}
                            onChange={(e) => setNewField({ ...newField, helpText: e.target.value })}
                            placeholder="e.g., We'll send updates via SMS"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Options for Select, Checkbox, Radio */}
                    {['select', 'checkbox', 'radio'].includes(newField.fieldType) && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Options (comma separated)
                            </label>
                            <input
                                type="text"
                                value={newField.options || ''}
                                onChange={(e) => setNewField({
                                    ...newField,
                                    options: e.target.value
                                })}
                                placeholder="e.g., Student, Professional, Other"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-2">
                        <button
                            type="button"
                            onClick={() => {
                                setIsAddingField(false);
                                setNewField({
                                    fieldName: '',
                                    fieldType: 'text',
                                    validation: 'required',
                                    placeholder: '',
                                    helpText: '',
                                });
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            <X className="w-5 h-5 inline mr-2" />
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleAddField}
                            disabled={!newField.fieldName || !newField.fieldType}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <Check className="w-5 h-5 inline mr-2" />
                            Add Field
                        </button>
                    </div>
                </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">Tips</h3>
                        <div className="mt-2 text-sm text-blue-700">
                            <ul className="list-disc list-inside space-y-1">
                                <li>Drag fields to reorder them as they'll appear in the form.</li>
                                <li>You can remove ANY field, including default Name and Email.</li>
                                <li>Use help text to guide participants in filling out the form.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DynamicFieldsStep;